import React from 'react';
import styles from './PrivacyPolicy.module.css';

const TermsAndConditions = () => {
    return (
        <div className={styles.privacyPage}>
            <div className={styles.container}>
                <header className={styles.header}>
                    <h1>Terms & Conditions</h1>
                    <p className={styles.lastUpdated}>Last updated: {new Date().toLocaleDateString()}</p>
                </header>
                
                <main className={styles.content}>
                    <section className={styles.section}>
                        <h2>1. Acceptance of Terms</h2>
                        <p>
                            By accessing, browsing, or using the services provided by Apexbit ("Company", "we", "us", or "our"), 
                            you ("Client", "you", or "your") acknowledge that you have read, understood, and agree to be bound 
                            by these Terms and Conditions. If you do not agree to these terms, you must not use our services.
                        </p>
                    </section>

                    <section className={styles.section}>
                        <h2>2. Services Description</h2>
                        <p>
                            Apexbit provides professional digital services including but not limited to:
                        </p>
                        <ul>
                            <li>Custom web development and web applications</li>
                            <li>UI/UX design and prototyping</li>
                            <li>Mobile application development (iOS & Android)</li>
                            <li>Digital marketing and SEO optimisation</li>
                            <li>Cloud solutions and deployment</li>
                            <li>Technical consulting and digital transformation</li>
                        </ul>
                        <p>
                            All projects are custom-quoted based on specific requirements outlined in individual project proposals.
                        </p>
                    </section>

                    <section className={styles.section}>
                        <h2>3. Project Engagement Process</h2>
                        <p><strong>3.1 Project Proposal</strong></p>
                        <p>
                            All projects begin with a detailed project proposal outlining scope, deliverables, timeline, 
                            and cost estimates. This proposal must be accepted in writing by both parties before work commences.
                        </p>
                        
                        <p><strong>3.2 Project Commencement</strong></p>
                        <p>
                            No work shall commence until:
                        </p>
                        <ul>
                            <li>A signed project agreement is received</li>
                            <li>The initial 30% deposit payment is processed and cleared</li>
                            <li>All required project assets and access credentials are provided by the Client</li>
                        </ul>
                    </section>

                    <section className={styles.section}>
                        <h2>4. Payment Terms</h2>
                        <p><strong>4.1 Deposit Requirement</strong></p>
                        <p>
                            A non-refundable deposit of 30% of the total project cost is required before any work commences. 
                            This deposit secures your project in our production schedule and covers initial setup and planning costs.
                        </p>
                        
                        <p><strong>4.2 Milestone Payments</strong></p>
                        <p>
                            For projects exceeding £5,000 or with timelines longer than 30 days, milestone payments may be 
                            established as follows:
                        </p>
                        <ul>
                            <li>30% deposit upon project initiation</li>
                            <li>40% upon completion of major milestones (as defined in project agreement)</li>
                            <li>30% upon final delivery and project completion</li>
                        </ul>
                        
                        <p><strong>4.3 Final Payment</strong></p>
                        <p>
                            The remaining 70% balance is due upon project completion and delivery of all final assets. 
                            Final deliverables will not be released until full payment is received and cleared.
                        </p>
                        
                        <p><strong>4.4 Late Payments</strong></p>
                        <p>
                            Payments overdue by more than 14 days will incur a late fee of 1.5% per month (18% APR) on the 
                            outstanding balance. Projects with overdue payments may be paused until payment is received.
                        </p>
                    </section>

                    <section className={styles.section}>
                        <h2>5. Project Timeline and Delays</h2>
                        <p><strong>5.1 Estimated Timelines</strong></p>
                        <p>
                            All project timelines are estimates based on current workload and project complexity. While we 
                            strive to meet all deadlines, they are not guaranteed delivery dates.
                        </p>
                        
                        <p><strong>5.2 Client-Caused Delays</strong></p>
                        <p>
                            The Company shall not be held responsible for delays caused by:
                        </p>
                        <ul>
                            <li>Late provision of required materials, content, or feedback</li>
                            <li>Client-requested changes to project scope</li>
                            <li>Delayed approvals or decision-making</li>
                            <li>Technical issues with third-party platforms or services</li>
                        </ul>
                        
                        <p><strong>5.3 Project Pauses</strong></p>
                        <p>
                            Projects paused by the Client for more than 30 days may be subject to reactivation fees and 
                            rescheduling based on current availability.
                        </p>
                    </section>

                    <section className={styles.section}>
                        <h2>6. Revisions and Change Requests</h2>
                        <p><strong>6.1 Included Revisions</strong></p>
                        <p>
                            Each project includes a specified number of revision rounds as outlined in the project proposal. 
                            Additional revisions beyond this allowance will be billed at our standard hourly rate.
                        </p>
                        
                        <p><strong>6.2 Scope Changes</strong></p>
                        <p>
                            Any changes to the project scope after approval may result in adjustments to the project timeline 
                            and cost. Scope change requests must be submitted in writing and approved by both parties.
                        </p>
                    </section>

                    <section className={styles.section}>
                        <h2>7. Intellectual Property Rights</h2>
                        <p><strong>7.1 Client Materials</strong></p>
                        <p>
                            The Client warrants that any materials provided to the Company (including text, images, logos, 
                            and other content) do not infringe upon any third-party rights and that the Client has obtained 
                            all necessary permissions for their use.
                        </p>
                        
                        <p><strong>7.2 Delivery of Rights</strong></p>
                        <p>
                            Upon receipt of full and final payment, the Company grants the Client:
                        </p>
                        <ul>
                            <li>Full ownership of the custom-developed code and design assets created specifically for the project</li>
                            <li>License to use, modify, and distribute the delivered work</li>
                            <li>All necessary files and documentation for ongoing maintenance</li>
                        </ul>
                        
                        <p><strong>7.3 Exclusions</strong></p>
                        <p>
                            The following are excluded from the transfer of rights:
                        </p>
                        <ul>
                            <li>Third-party components, libraries, or frameworks with their own licensing terms</li>
                            <li>Company's proprietary frameworks, tools, and methodologies</li>
                            <li>Source code for any pre-existing Company products or templates</li>
                        </ul>
                        
                        <p><strong>7.4 Portfolio Rights</strong></p>
                        <p>
                            The Company retains the right to display completed work in its portfolio, marketing materials, 
                            and case studies unless otherwise specified in a separate non-disclosure agreement.
                        </p>
                    </section>

                    <section className={styles.section}>
                        <h2>8. Confidentiality</h2>
                        <p>
                            Both parties agree to maintain the confidentiality of any proprietary or sensitive information 
                            shared during the project. This obligation survives termination of the agreement.
                        </p>
                    </section>

                    <section className={styles.section}>
                        <h2>9. Warranties and Representations</h2>
                        <p><strong>9.1 Company Warranties</strong></p>
                        <p>
                            The Company warrants that:
                        </p>
                        <ul>
                            <li>Services will be performed in a professional and workmanlike manner</li>
                            <li>Deliverables will substantially conform to the specifications in the project agreement</li>
                            <li>The work will not knowingly infringe upon any third-party intellectual property rights</li>
                        </ul>
                        
                        <p><strong>9.2 Client Warranties</strong></p>
                        <p>
                            The Client warrants that:
                        </p>
                        <ul>
                            <li>They have the authority to enter into this agreement</li>
                            <li>All provided materials are legally obtained and licensed for use</li>
                            <li>They will provide timely feedback and necessary access to complete the project</li>
                        </ul>
                    </section>

                    <section className={styles.section}>
                        <h2>10. Limitation of Liability</h2>
                        <p>
                            In no event shall the Company's total liability to the Client for all damages, losses, and causes 
                            of action (whether in contract, tort, or otherwise) exceed the total amount paid by the Client 
                            to the Company under this agreement.
                        </p>
                        <p>
                            The Company shall not be liable for any indirect, incidental, special, consequential, or punitive 
                            damages, including but not limited to loss of profits, data, business opportunities, or goodwill.
                        </p>
                    </section>

                    <section className={styles.section}>
                        <h2>11. Termination</h2>
                        <p><strong>11.1 Termination for Cause</strong></p>
                        <p>
                            Either party may terminate this agreement with written notice if the other party breaches any 
                            material term and fails to cure such breach within 14 days of receiving notice.
                        </p>
                        
                        <p><strong>11.2 Termination by Client</strong></p>
                        <p>
                            If the Client terminates the project after work has commenced:
                        </p>
                        <ul>
                            <li>The 30% deposit is non-refundable</li>
                            <li>The Client will be invoiced for all work completed up to the termination date</li>
                            <li>Any delivered work will be provided "as-is" without warranty</li>
                        </ul>
                    </section>

                    <section className={styles.section}>
                        <h2>12. Force Majeure</h2>
                        <p>
                            Neither party shall be liable for any failure or delay in performance under this agreement due 
                            to circumstances beyond their reasonable control, including but not limited to acts of God, 
                            government restrictions, wars, insurrections, pandemics, and/or any other cause beyond the 
                            reasonable control of the party whose performance is affected.
                        </p>
                    </section>

                    <section className={styles.section}>
                        <h2>13. Governing Law and Dispute Resolution</h2>
                        <p><strong>13.1 Governing Law</strong></p>
                        <p>
                            This agreement shall be governed by and construed in accordance with the laws of England and Wales.
                        </p>
                        
                        <p><strong>13.2 Dispute Resolution</strong></p>
                        <p>
                            In the event of any dispute, the parties agree to first attempt to resolve the matter through 
                            informal negotiation. If unresolved, the parties agree to mediate the dispute before pursuing 
                            litigation.
                        </p>
                    </section>

                    <section className={styles.section}>
                        <h2>14. Miscellaneous</h2>
                        <p><strong>14.1 Entire Agreement</strong></p>
                        <p>
                            This document constitutes the entire agreement between the parties and supersedes all prior 
                            communications, agreements, and understandings.
                        </p>
                        
                        <p><strong>14.2 Amendments</strong></p>
                        <p>
                            Any amendments to this agreement must be in writing and signed by both parties.
                        </p>
                        
                        <p><strong>14.3 Severability</strong></p>
                        <p>
                            If any provision of this agreement is found to be invalid or unenforceable, the remaining 
                            provisions shall remain in full force and effect.
                        </p>
                    </section>
                </main>
            </div>
        </div>
    );
};

export default TermsAndConditions;